/** @file       operations/accountoperations.js
 *  @author     Alex Huctwith <alex@kingsds.network>
 *
 *  @date       April 2022
 *
 *              Portal client operations related to handling login and session
 */

import PKCE from 'js-pkce';
import i18n from 'i18next';

import { debugging } from '@/lib/debugging';
import { ErrorOperations } from '@/operations/ErrorOperations';

/* oAuth configuration is in two places:
 * - dcpConfig.oAuth        - oAuth configuration for the system
 * - dcpConfig.portal.oAuth - oAuth specifics for the portal
 *
 * They are merged together, with resultant object having portal-specific overrides on the system object
 * within the portal application(s). This must match the Doorkeeper configuration on the oAuth server.
 */
const oAuth = Object.assign({}, dcpConfig.oAuth, dcpConfig.portal.oAuth);
const pkce = new PKCE({
  client_id:              oAuth.clientId,
  redirect_uri:           new URL('/auth/callback', window.location.origin).href,
  authorization_endpoint: new URL(oAuth.authorizationPath, oAuth.location).href,
  token_endpoint:         new URL(oAuth.accessTokenPath,   oAuth.location).href,
  requested_scopes:       'login',
});

export class LoginOperations {
  static #loginPromiseHnd = false;
  static #userRecord = false;

  static async getUserRecord()
  {
    if (!this.p$portalDb)
      this.p$portalDb = dcp.jnu.connect('portal');
    window.dcp.utils.prefs.setDbConnection(this.p$portalDb);
    if (!LoginOperations.#userRecord)
    {
      const db = await this.p$portalDb;
      const cursor = await db.select({ table: 'user', prototype: {} });
      if (cursor.rowCount !== 1)
      {
        await dcp['dom-tk'].modals.alert(new Error('Unable to retrieve user record'));
        await this.handleLogout();
      }
      LoginOperations.#userRecord = await cursor.getFirst();
    }
    return LoginOperations.#userRecord;
  }

  static async getRootIdentityAddress()
  {
    return (await LoginOperations.getUserRecord()).owner;
  }

  /**
   * Logs the user of of their current session.
   *
   * The user is redirected to the signout page on the OAuth server. Eventually, the Oauth server makes a request to the proxy auth service
   * to delete the user's proxy identity from the database, hence the need to send the proxy identity as a search parameter in the redirect.
   */
  static async handleLogout()
  {
    const signoutUrl = new URL('/signout', oAuth.location.origin);

    /* note --proxyAddress will no longer be required by oauth server as of june 2026 /wg */
    const ks = dcp.identity.check() && dcp.identity.get();
    if (ks)
      signoutUrl.searchParams.set('proxy', ks.address);
    const oauthToken = sessionStorage.getItem('oauth_token');
    if (oauthToken)
      signoutUrl.searchParams.set('token', oauthToken);
    signoutUrl.searchParams.set('redirect', dcpConfig.portal.location.href);
    signoutUrl.searchParams.set('time', Date.now()); /* cache-buster */
    await dcp.identity.logout();
    sessionStorage.clear();
    await dcp['dom-tk'].modals.afterModalsNavigateTo(signoutUrl);
  }

  /**
   * Renew proxy keystore.
   *
   * It sends a request to the oAuth component renew method to renew the proxy keys to keep the portal
   * DCP connection alive.
   */
  static async renewPxAuth(refreshToken, renewMs)
  {
    const response = await pkce.refreshAccessToken(refreshToken);
    sessionStorage.setItem('oauth_token', response.access_token);

    const id = dcp.identity.get();
    const body = {
      expiry: Math.floor(Date.now()/1000) + 3600,
      renewAmount: Math.ceil(renewMs/1000),
      endpoint: dcpConfig.pxAuth.services.althea.location.resolve('renew'),
    };
    await dcp.utils.submitSignedPost(id, body);
  }

  /**
   * Wrapper for #handleLogin() which ensures it is only every called once but always returns the same
   * promise. Code needing to access resources guarded by the login operation should await this promise.
   */
  static handleLogin(usePx)
  {
    if (!LoginOperations.#loginPromiseHnd)
    {
      LoginOperations.#loginPromiseHnd = Promise.withResolvers();
      (usePx ? this.#handlePxLogin() : this.#handleOAuthLogin())
        .then(LoginOperations.#loginPromiseHnd.resolve)
        .catch(LoginOperations.#loginPromiseHnd.reject);
    }
    return LoginOperations.#loginPromiseHnd.promise;
  }

  /**
   * Handle a login via proxy key instead of oAuth. Features that require querying the oAuth database
   * (eg. user profile) will not work.
   */
  static async #handlePxLogin()
  {
    var ks;

    sessionStorage.removeItem('oauth_token');
    sessionStorage.removeItem('pkce_state');
    sessionStorage.removeItem('pkce_code_verifier');

    do
    {
      ks = await dcp['dom-tk'].modals.uploadKeystore('Upload Login Keystore', { close: false });
      if (ks === null)
        return this.#handleOAuthLogin();
      if (ks)
      {
        try
        {
          const pk = await ks.getPrivateKey();
          ks = await new dcp.wallet.Keystore(pk, '');
        }
        catch(error)
        {
          ks = false;
        }
      }
    } while (!ks);

    await dcp.identity.set(ks);
    prepareDCPConnections();
  }

  /**
   * Login handler function.
   *
   * Sweet summer child, let me tell you how this really works. When the js bundle is loaded for the first time, we call `this.load()` before
   * all other react bootstrapping. Then in most cases, it won't have search params, we use this proxy condition to indicate that we should
   * attempt a login. When we are redirected back to `/auth/callback?<stuff>`, we _will_ have search params and we will perform the
   * authorization token to access token exchange, get the one piece of information we need and throw the access code away. Only then do the
   * logic in /auth/callback take effect and redirect us to what we were intending all along.
   *
   * The special case is when a 'keystore' is provided via search params, then we don't even attempt to login at all. We just assume the an
   * `oAuthToken` will be provided in search param as well, use that to unlock the keystore and proceed.
   *
   * This is some extremely mutation heavy imperative code that only works because we load it before all other react bootstrapping is done.
   */
  static async #handleOAuthLogin()
  {
    debugging('login') && console.debug('PKCE config:', pkce.config);

    const urlParams = new URLSearchParams(window.location.search);
    const keystore = urlParams.get('keystore');
    const code = urlParams.get('code');
    const pathNameWithParams = window.location.pathname + window.location.search + window.location.hash;

    // Redirects user to route with authorization code
    if (!code)
    {
      await attemptAuthorization();
    }
    else if (!sessionStorage.getItem('pkce_state') || !sessionStorage.getItem('pkce_code_verifier'))
    {
      await reattemptAuthorization();
    }
    else
    {
      const url = window.location.href;
      let resp;
      debugging('login') && console.debug(`Exchanging authorization code for access token using ${url}`);
      try
      {
        resp = await pkce.exchangeForAccessToken(url);
      }
      catch(error)
      {
        /* Empty respsonse here normally means a 500 error in the PKCE XHR */
        let errMsg = '';
        console.error('pkce.exchangeForAccessToken invalid response', resp);
        if (resp?.error)
          errMsg = ': ' + resp.error;
        errMsg += '\n\n' + 'Login server: ' + pkce.config.token_endpoint;
        await dcp['dom-tk'].modals.alert(`Retrieving login failed` + errMsg);
        await this.handleLogout();
      }

      debugging('login') && console.debug('OAuth server response:', resp);
      if (!resp)
        console.error(`No response (500?) from "${pkce.config.token_endpoint}" for access token request`);
      else if (resp.error)
      {
        const endpointUrl = new URL(pkce.config.token_endpoint);
        let errorMessage = `Unable to retrieve access token from ${endpointUrl.origin}: ${resp.error}`;
        if (resp.error_description)
          errorMessage += `\n${resp.error_description}`;
        console.error(`Error in response from ${pkce.config.token_endpoint}:`, resp);
        await dcp['dom-tk'].modals.alert(errorMessage, { title: 'Login Failure' });
      }
      if (!resp || resp.error)
        await this.handleLogout();

      try
      {
        sessionStorage.setItem('oauth_token', resp.access_token);
        await i18n.changeLanguage(resp.locale);
        debugging('login') && console.debug('Current i18n resolved language:', i18n.resolvedLanguage);

        /* Set the identity after DCP connections are plumbed - work around bug where some code waits
         * for an identity and immediately tries to make a request.
         */
        const idKs = await new dcp.wallet.IdKeystore(resp.keystore);
        await dcp.identity.set(idKs, { passphrase: resp.access_token });

        // set up connections
        debugging('login') && console.debug('Setting up connections to the portal and bank');
        prepareDCPConnections();

        // make oauth token, user email, and user creation date available globally
        window.user_email = resp.email;
        window.user_creation_date = resp.creationDate?.slice(0,10);

        let timeOut = 45 * 60 * 1000;
        if (dcp['dcp-build'].config.build === 'debug')
          timeOut = 5 * 60 * 1000;

        // Client side timeout
        setInterval(() => {
          this.renewPxAuth(resp.refresh_token, timeOut)
        }, [timeOut]);

        debugging('login') && console.debug('Login successful');
      }
      catch(error)
      {
        await dcp['dom-tk'].modals.alert(error, { title: 'Unable to login', buttons: [ 'Logout' ] });
        await this.handleLogout();
      }
    }

    /**
     * Navigates the window to the authorization endpoint to begin the oAuth flow.
     */
    async function attemptAuthorization()
    {
      if (!oAuth?.clientId || typeof oAuth.clientId !== 'string' || oAuth.clientId.length < 32)
      {
        await dcp['dom-tk'].modals.alert(`Invalid oAuth clientId '${dcpConfig.oAuth?.clientId}'`,
                                         { title: 'Unable to login', buttons: [ 'Reload' ] });
        window.location.reload(true);
      }
      else
      {
        const url = pkce.authorizeUrl({ state: pathNameWithParams });
        await dcp['dom-tk'].modals.afterModalsNavigateTo(url);
      }
    }

    /**
     * Navigates the window to the authorization endpoint again to re-attempt
     * the oAuth flow.
     *
     * Necessary because we probably have a state variable that isn't valid for
     * our session.
     */
    async function reattemptAuthorization()
    {
      sessionStorage.removeItem('pkce_state');
      sessionStorage.removeItem('pkce_code_verifier');
      await attemptAuthorization();
    }

    return false;
  }
}

function prepareDCPConnections()
{
  function openPortalConnection()
  {
    window.portalConnection = new dcp.protocol.Connection(window.dcpConfig.portal.services.accountManager);
    window.portalConnection.on('end', openPortalConnection);
    debugging('login') && console.debug('New portal connection,', window.portalConnection.debugLabel);
  }
  function openBankConnection()
  {
    window.bankTellerConnection = new dcp.protocol.Connection(window.dcpConfig.bank.services.bankTeller);
    window.bankTellerConnection.on('end', openBankConnection);
    debugging('login') && console.debug('New bank connection,', window.bankTellerConnection.debugLabel);
  }
  openPortalConnection();
  openBankConnection();
}
